import {
  CurrencyExchangeSettlementDateResponse,
  ExchangeDirection,
  ExchangeOrderTemporaryResponse,
  ExchangeOrderValidationResponse,
  ExchangeRateResponse,
  Loadable,
  LoadableState,
  TBalance,
} from '@eih-netbroker/common';
import { createContext, useCallback, useState } from 'react';

export enum CurrencyExchangeStep {
  Params = 'params',
  Summary = 'summary',
}

export enum ExchangeLoaderType {
  CalculatingTargetAmountFromSource = 'CalculatingTargetAmountFromSource',
  CalculatingSourceAmountFromTarget = 'CalculatingSourceAmountFromTarget',
  CalculatingBothSourceAndTarget = 'CalculatingBothSourceAndTarget',
  RefreshingExchangeRateInSummary = 'RefreshingExchangeRateInSummary',
  ExchangeRateInProgress = 'ExchangeRateInProgress',
  TwoFactorConfirmInProgress = 'TwoFactorConfirmInProgress',
}

/** https://ersteinv.atlassian.net/wiki/spaces/Projects/pages/39618753/Netbroker+lehets+ges+deviz+k */
export const CurrencyDominance: Record<string, string> = {
  HUFCHF: 'HUF',
  HUFEUR: 'HUF',
  HUFGBP: 'HUF',
  HUFJPY: 'HUF',
  HUFPLN: 'HUF',
  HUFUSD: 'HUF',

  USDCHF: 'CHF',
  USDEUR: 'USD',
  USDGBP: 'USD',
  USDHUF: 'HUF',
  USDJPY: 'JPY',

  EURCHF: 'EUR',
  EURGBP: 'EUR',
  EURHUF: 'HUF',
  EURUSD: 'USD',
  EURJPY: 'JPY',
  EURPLN: 'PLN',

  GBPCHF: 'CHF',
  GBPEUR: 'GBP',
  GBPHUF: 'HUF',
  GBPUSD: 'USD',

  CHFEUR: 'EUR',
  CHFHUF: 'HUF',
  CHFGBP: 'CHF',
  CHFUSD: 'USD',

  PLNEUR: 'EUR',
  PLNHUF: 'HUF',

  JPYEUR: 'JPY',
  JPYHUF: 'HUF',
  JPYUSD: 'JPY',
};

export type CurrencyExchangeContextProps =
  | undefined
  | {
      selectedSource: string | null;
      setSelectedSource: React.Dispatch<React.SetStateAction<string | null>>;
      selectedTarget: string | null;
      setSelectedTarget: React.Dispatch<React.SetStateAction<string | null>>;
      sourceAmount: number | undefined;
      setSourceAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
      targetAmount: number | undefined;
      setTargetAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
      selectedExchangeDirection: ExchangeDirection | undefined;
      setSelectedExchangeDirection: React.Dispatch<React.SetStateAction<ExchangeDirection | undefined>>;
      currencyBalance: LoadableState<TBalance>;
      setCurrencyBalance: React.Dispatch<React.SetStateAction<LoadableState<TBalance>>>;
      isTDayExchange: boolean;
      setIsTDayExchange: React.Dispatch<React.SetStateAction<boolean>>;
      loaderState: ExchangeLoaderType | null;
      setLoaderState: React.Dispatch<React.SetStateAction<ExchangeLoaderType | null>>;
      clearLoaderState: () => void;
      tBalanceWarning: boolean;
      setTBalanceWarning: React.Dispatch<React.SetStateAction<boolean>>;
      exchangeRate: LoadableState<ExchangeRateResponse>;
      setExchangeRate: React.Dispatch<React.SetStateAction<LoadableState<ExchangeRateResponse>>>;
      isExchangeRateHasUpdated: boolean;
      setIsExchangeRateHasUpdated: React.Dispatch<React.SetStateAction<boolean>>;
      isCurrencyExchangeDisabled: (twoFactorCode: string) => boolean;
      isCurrencyExchangeEnabled: boolean;
      setIsCurrencyExchangeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
      settlementDate: LoadableState<CurrencyExchangeSettlementDateResponse>;
      setSettlementDate: React.Dispatch<React.SetStateAction<LoadableState<CurrencyExchangeSettlementDateResponse>>>;
      tempOrder: ExchangeOrderTemporaryResponse | null;
      setTempOrder: React.Dispatch<React.SetStateAction<ExchangeOrderTemporaryResponse | null>>;
      twoFactorConfirmResponse: ExchangeOrderValidationResponse['message'] | null;
      setTwoFactorConfirmResponse: React.Dispatch<
        React.SetStateAction<ExchangeOrderValidationResponse['message'] | null>
      >;
      currencyExchangeStep: CurrencyExchangeStep;
      setCurrencyExchangeStep: React.Dispatch<React.SetStateAction<CurrencyExchangeStep>>;
      validationError: { msg: string } | null;
      setValidationError: React.Dispatch<{ msg: string } | null>;
      clearValidationError: () => void;
    };

export const CurrencyExchangeContext = createContext<CurrencyExchangeContextProps>(undefined);

export const CurrencyExchangeProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [isTDayExchange, setIsTDayExchange] = useState(false);

  const [sourceAmount, setSourceAmount] = useState<number>();
  const [targetAmount, setTargetAmount] = useState<number>();
  const [currencyBalance, setCurrencyBalance] = useState<LoadableState<TBalance>>(Loadable.uninitialized);
  const [exchangeRate, setExchangeRate] = useState<LoadableState<ExchangeRateResponse>>(Loadable.uninitialized);
  const [selectedExchangeDirection, setSelectedExchangeDirection] = useState<ExchangeDirection>();
  const [loaderState, setLoaderState] = useState<ExchangeLoaderType | null>(null);
  const [tBalanceWarning, setTBalanceWarning] = useState(false);
  const [isExchangeRateHasUpdated, setIsExchangeRateHasUpdated] = useState(false);
  const [isCurrencyExchangeEnabled, setIsCurrencyExchangeEnabled] = useState(false);
  const [settlementDate, setSettlementDate] = useState<LoadableState<CurrencyExchangeSettlementDateResponse>>(
    Loadable.uninitialized,
  );
  const [tempOrder, setTempOrder] = useState<ExchangeOrderTemporaryResponse | null>(null);
  const [twoFactorConfirmResponse, setTwoFactorConfirmResponse] = useState<
    ExchangeOrderValidationResponse['message'] | null
  >(null);
  const [currencyExchangeStep, setCurrencyExchangeStep] = useState<CurrencyExchangeStep>(CurrencyExchangeStep.Params);
  const [validationError, setValidationError] = useState<{ msg: string } | null>(null);

  const clearLoaderState = () => setLoaderState(null);

  const clearValidationError = () => setValidationError(null);

  const isCurrencyExchangeDisabled = useCallback(
    (twoFactorCode: string) => {
      if (
        !selectedSource ||
        !selectedTarget ||
        !sourceAmount ||
        !targetAmount ||
        !settlementDate.isLoaded ||
        !exchangeRate.isLoaded ||
        (tempOrder && !twoFactorCode.trim())
      ) {
        return true;
      }
      return false;
    },
    [selectedSource, selectedTarget, sourceAmount, targetAmount, settlementDate, exchangeRate, tempOrder],
  );

  const value = {
    selectedSource,
    setSelectedSource,
    selectedTarget,
    setSelectedTarget,
    sourceAmount,
    setSourceAmount,
    targetAmount,
    setTargetAmount,
    selectedExchangeDirection,
    setSelectedExchangeDirection,
    currencyBalance,
    setCurrencyBalance,
    loaderState,
    setLoaderState,
    clearLoaderState,
    tBalanceWarning,
    setTBalanceWarning,
    exchangeRate,
    setExchangeRate,
    isExchangeRateHasUpdated,
    setIsExchangeRateHasUpdated,
    isCurrencyExchangeDisabled,
    isCurrencyExchangeEnabled,
    setIsCurrencyExchangeEnabled,
    isTDayExchange,
    setIsTDayExchange,
    settlementDate,
    setSettlementDate,
    tempOrder,
    setTempOrder,
    twoFactorConfirmResponse,
    setTwoFactorConfirmResponse,
    currencyExchangeStep,
    setCurrencyExchangeStep,
    validationError,
    setValidationError,
    clearValidationError,
  };

  return <CurrencyExchangeContext.Provider value={value}>{children}</CurrencyExchangeContext.Provider>;
};
